import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.REGIONS;

const EditRegionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [region, setRegion] = useState({
    name: "",
    publish: false,
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [redirectOnClose, setRedirectOnClose] = useState(false);

  useEffect(() => {
    fetchRegion();
  }, []);

  const fetchRegion = async () => {
    try {
      const response = await axios.get(API_URL.EDIT(id));
      setRegion({
        name: response.data.region.name || "",
        publish: response.data.region.publish || false,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching region:", error);
      setSnackbar({
        open: true,
        message: "Failed to load region data",
        severity: "error",
      });
      setRedirectOnClose(false);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setRegion({ ...region, [e.target.name]: e.target.value });
  };

  const handlePublishToggle = () => {
    setRegion((prev) => ({ ...prev, publish: !prev.publish }));
  };

  const handleUpdate = async () => {
    try {
      await axios.put(API_URL.EDIT(id), region);
      setSnackbar({
        open: true,
        message: "Region updated successfully!",
        severity: "success",
      });
      setRedirectOnClose(true);
    } catch (error) {
      console.error("Error updating region:", error);
      setSnackbar({
        open: true,
        message: "Failed to update region: " + (error.response?.data?.message || "Something went wrong"),
        severity: "error",
      });
      setRedirectOnClose(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (redirectOnClose) {
      navigate(-1); // Go back
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  return (
    <>
      <Card sx={{ p: 4, maxWidth: 500, mx: "auto", mt: 5 }}>
        <Typography variant="h5" mb={3}>
          Edit Region
        </Typography>
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            label="Region Name"
            name="name"
            value={region.name}
            onChange={handleChange}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={region.publish}
                onChange={handlePublishToggle}
                color="primary"
              />
            }
            label="Published"
          />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Button variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditRegionForm;
